"use client"

import { usePathname } from "next/navigation"
import { useEffect } from "react"
import { isSsoSilentPath } from "./sso-probe-store"

/**
 * Reports a FAILED silent SSO probe from inside the hidden iframe.
 *
 * The happy path is self-reporting: `/sso-silent?done=1` posts `ra-sso-done` once NextAuth
 * has set the session cookie. The failure path had nothing — on `login_required` raauth
 * bounces the iframe to NextAuth's configured error page (`/v2/unified/login`) and the flow
 * just stops. The parent only noticed via the 8s give-up timer, which means an anonymous
 * visitor would stare at a navbar skeleton for eight seconds.
 *
 * So: any RA page that finds itself rendering inside our own iframe, other than the
 * `/sso-silent` worker itself, means the silent flow landed somewhere it shouldn't have.
 * Report it and let the parent commit to "logged out" immediately.
 *
 * ⚠ The `/sso-silent` exclusion is load-bearing and MUST be trailing-slash tolerant. This
 * app sets `trailingSlash: true`, so the worker's real pathname is `/sso-silent/` — an exact
 * `=== "/sso-silent"` comparison is false there, which made this component fire `ok: false`
 * from inside the worker itself, settling the probe and tearing down the iframe before raauth
 * could ever answer. That silently broke every silent sign-in. Hence isSsoSilentPath().
 *
 * Mirror image of SilentSsoCheck, which only acts when NOT in an iframe. Mounting both in
 * the root layout covers the parent document and the iframe document with one tree.
 */
export function SsoSilentReporter() {
   const pathname = usePathname()

   useEffect(() => {
      if (typeof window === "undefined" || window.self === window.top) return
      if (isSsoSilentPath(pathname)) return
      // Same-origin only. postMessage targets our own origin, so a cross-origin embedder
      // (RA pages aren't meant to be embedded) never receives this.
      window.parent.postMessage({ type: "ra-sso-done", ok: false }, window.location.origin)
   }, [pathname])

   return null
}
